/**
 * ============================================================
 * 权限判断工具
 * ============================================================
 * 本文件定义了系统中三种角色的权限规则：
 * - super_admin（超级管理员）：全权限，可操作所有状态流转
 * - china_admin（中国管理员）：操作录入和到港，待派送只能加备注
 * - worker（工人）：下载已到港数据、处理待派送和海外仓
 * ============================================================
 */

/** 角色定义常量 */
export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  CHINA_ADMIN: 'china_admin',
  WORKER: 'worker'
}

/** 角色显示信息（用于UI展示） */
export const ROLE_INFO = {
  super_admin: {
    name: '超级管理员',
    namePt: 'Super Administrador',
    avatar: '超',
    fullAccess: true
  },
  china_admin: {
    name: '中国管理员',
    namePt: 'Administrador Chinês',
    avatar: '中',
    fullAccess: true
  },
  worker: {
    name: '工人',
    namePt: 'Operador',
    avatar: '工',
    fullAccess: false
  }
}

/**
 * 检查当前用户是否为超级管理员
 */
export function isSuperAdmin(role) {
  return role === ROLES.SUPER_ADMIN
}

/**
 * 检查当前用户是否为中国管理员
 */
export function isChinaAdmin(role) {
  return role === ROLES.CHINA_ADMIN
}

/**
 * 检查当前用户是否为工人
 */
export function isWorker(role) {
  console.log(role)
  return role === ROLES.WORKER
}

/**
 * 检查当前用户是否为管理员（超级管理员或中国管理员）
 */
export function isAdmin(role) {
  return isSuperAdmin(role) || isChinaAdmin(role)
}

/**
 * 获取角色的权限配置
 * @param {string} role - 角色标识
 * @param {Object} [userPermissions] - 后端返回的权限配置（可选）
 * @returns {Object} 权限配置对象
 */
export function getRolePermissions(role, userPermissions) {
  return userPermissions && typeof userPermissions === 'object' ? userPermissions : {}
}

/**
 * 获取指定状态的权限配置
 * @param {string} role - 角色标识
 * @param {string} status - 状态标识
 * @param {Object} [userPermissions] - 后端返回的权限配置（可选）
 * @returns {Object} 状态权限配置
 */
export function getStatusPermission(role, status, userPermissions) {
  const permissions = getRolePermissions(role, userPermissions)
  return permissions.statusPermissions?.[status] || { readable: true, writable: false, actions: [] }
}

/**
 * 判断是否可以执行某个操作
 * @param {string} role - 角色标识
 * @param {string} status - 状态标识
 * @param {string} action - 操作标识
 * @param {Object} [userPermissions] - 后端返回的权限配置（可选）
 * @returns {boolean} 是否可以执行
 */
export function canPerformAction(role, status, action, userPermissions) {
  const perm = getStatusPermission(role, status, userPermissions)
  return perm.actions && perm.actions.includes(action)
}

/**
 * 判断字段是否可见
 * @param {string} role - 角色标识
 * @param {string} field - 字段名称
 * @param {Object} [userPermissions] - 后端返回的权限配置（可选）
 * @returns {boolean} 字段是否可见
 */
export function hasFieldVisibility(role, field, userPermissions) {
  const permissions = getRolePermissions(role, userPermissions)
  return permissions.fieldVisibility?.[field] !== false
}

/**
 * 检查路由是否可访问
 * @param {string} routePath - 路由路径
 * @param {string} role - 用户角色
 * @param {Array<string>} [accessibleRoutes] - 动态可访问路由（从 getRouters 接口获取）
 * @param {Object} [userPermissions] - 后端返回的权限配置（可选）
 */
export function canAccessRoute(routePath, role, accessibleRoutes, userPermissions) {
  if (isSuperAdmin(role)) return true

  const permissions = getRolePermissions(role, userPermissions)
  const routes = accessibleRoutes && accessibleRoutes.length > 0
    ? accessibleRoutes
    : (permissions.routes || [])

  return routes.some(r => routePath.startsWith(r)) || routePath === '/login'
}

/**
 * 脱敏数据 - 移除总货值相关字段
 */
export function desensitizeShipment(shipment) {
  const clone = { ...shipment }
  delete clone.totalValue
  return clone
}

/**
 * 脱敏列表
 */
export function desensitizeShipments(shipments) {
  return shipments.map(s => desensitizeShipment(s))
}